import type { IComponentController } from "angular";

import type { StateService } from "@uirouter/angularjs";
import { some as lodashSome } from "lodash";

import type { IProfileService } from "App/Modules/Auth";
import type { IProfile, Permission } from "App/Modules/Models";

import type { ILoginService } from "App/Modules/Auth/Services/Login/ILoginService";
import type { IMmState } from "../../../Core/Interfaces";
import type { IMainMenuItem } from "./IMainMenuItem";

export class MainMenuComponentController implements IComponentController {
    public static $inject: string[] = ["mmProfileService", "$state", "mmLoginService"];

    public isDisabled: boolean;

    public menuItems: IMainMenuItem[] = [];

    public profile: IProfile;

    private allMenuItems: IMainMenuItem[] = [];

    constructor(
        private readonly profileService: IProfileService,
        private readonly stateService: StateService,
        private readonly loginService: ILoginService,
    ) {}

    public $onInit() {
        this.profile = null;

        this.profileService.profileObservable().subscribe((profile: IProfile) => {
            this.profile = profile;
        });

        this.allMenuItems = [
            {
                state: "dashboard",
                icon: "home",
                caption: "BeforeLogin.Header.MainMenuHome",
            },

            {
                state: "teams",
                icon: "users",
                caption: "BeforeLogin.Team.Teams",
            },
            {
                state: "companies",
                icon: "briefcase",
                caption: "BeforeLogin.Company.Companies",
            },
            {
                state: "partners",
                icon: "handshake",
                caption: "BeforeLogin.Partner.Partners",
            },

            {
                state: "equipment",
                icon: "video",
                caption: "Entities.Equipment.EquipmentList",
            },
            {
                state: "equipmentTypes",
                icon: "wrench",
                caption: "BeforeLogin.EquipmentType.EquipmentTypes",
            },
            {
                state: "manufacturers",
                icon: "industry",
                caption: "BeforeLogin.Manufacturer.Manufacturers",
            },
            {
                state: "translationKeys",
                icon: "globe",
                caption: "BeforeLogin.TranslationKey.TranslationKeys",
            },
            {
                state: "translations",
                icon: "globe",
                caption: "BeforeLogin.Translation.Translations",
            },

            {
                state: "alarmCentrals",
                icon: "desktop",
                caption: "BeforeLogin.AlarmCentral.AlarmCentrals",
            },
        ];

        this.profileService.profileObservable().subscribe((profile: IProfile) => {
            this.menuItems = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const menuItem of this.allMenuItems) {
                const menuItemState = this.stateService.get(menuItem.state) as IMmState;
                let isAuthorized = false;

                if (
                    (
                        menuItemState.permission as {
                            allowAnonymous: boolean;
                        }
                    ).allowAnonymous
                ) {
                    isAuthorized = true;
                } else if (
                    (
                        menuItemState.permission as {
                            allowAnonymous: boolean;
                        }
                    ).allowAnonymous === false
                ) {
                    isAuthorized = !!profile;
                } else if (
                    (
                        menuItemState.permission as {
                            partnerPermission: Permission;
                        }
                    ).partnerPermission
                ) {
                    const { partnerPermission } = menuItemState.permission as {
                        partnerPermission: Permission;
                    };
                    isAuthorized =
                        profile &&
                        lodashSome(profile.userPermissions.partnerPermissions, (p) => p === partnerPermission);
                } else if (
                    (
                        menuItemState.permission as {
                            sitePermission: Permission;
                        }
                    ).sitePermission
                ) {
                    const { sitePermission } = menuItemState.permission as {
                        sitePermission: Permission;
                    };

                    let additionalSitePermission: Permission = null;

                    if (
                        (
                            menuItemState.permission as {
                                additionalSitePermission: Permission;
                            }
                        ).additionalSitePermission
                    ) {
                        // eslint-disable-next-line prefer-destructuring
                        additionalSitePermission = (
                            menuItemState.permission as {
                                additionalSitePermission: Permission;
                            }
                        ).additionalSitePermission;
                    }

                    isAuthorized =
                        profile &&
                        (lodashSome(
                            profile.userPermissions.sitePermissions,
                            (p) => p === sitePermission || p === additionalSitePermission,
                        ) ||
                            lodashSome(
                                profile.userPermissions.partnerPermissions,
                                (p) => p === sitePermission || p === additionalSitePermission,
                            ));
                }

                if (
                    !isAuthorized &&
                    (
                        menuItemState.permission as {
                            additionalPartnerPermission: Permission;
                        }
                    ).additionalPartnerPermission
                ) {
                    const { additionalPartnerPermission } = menuItemState.permission as {
                        additionalPartnerPermission: Permission;
                    };
                    isAuthorized =
                        profile &&
                        lodashSome(
                            profile.userPermissions.partnerPermissions,
                            (p) => p === additionalPartnerPermission,
                        );
                }

                if (isAuthorized) {
                    this.menuItems.push(menuItem);
                }
            }
        });
    }

    public logout() {
        this.loginService.logout();
        this.stateService.go("login");
    }
}
